import {
    Button,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "frontend-components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => Promise<void> | void;
    title: string;
    description: string;
    confirmLabel?: string;
}

export function ConfirmDialog({
    open,
    onOpenChange,
    onConfirm,
    title,
    description,
    confirmLabel,
}: ConfirmDialogProps) {
    const { t } = useTranslation();
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setError(null);
            setIsPending(false);
        }
    }, [open]);

    const handleConfirm = async () => {
        setIsPending(true);
        setError(null);
        try {
            await onConfirm();
            onOpenChange(false);
        } catch {
            setError(t("common.somethingWentWrong"));
        } finally {
            setIsPending(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!isPending) onOpenChange(v);
            }}
        >
            <DialogContent
                className="bg-compendia-card max-w-md text-compendia-foreground"
                aria-describedby="confirm-dialog-description"
            >
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <p id="confirm-dialog-description" className="text-body text-compendia-foreground">
                    {description}
                </p>
                {error && <p className="text-body-sm text-chameleon-pink">{error}</p>}
                <DialogFooter>
                    <div className="flex justify-end gap-2 w-full">
                        <Button
                            variant="outline"
                            type="button"
                            disabled={isPending}
                            onClick={() => onOpenChange(false)}
                        >
                            {t("common.cancel")}
                        </Button>
                        <Button
                            variant="fill"
                            type="button"
                            disabled={isPending}
                            onClick={handleConfirm}
                        >
                            {confirmLabel ?? t("common.ok")}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
